/**
 * React Query Client Configuration
 *
 * Configurazione centralizzata del QueryClient con opzioni ottimizzate
 * per caching, retry logic e gestione errori.
 */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import type { ReactNode } from 'react';
import { QUERY_CONFIG } from './constants';
import { isDev } from './env';

/**
 * Istanza singleton del QueryClient
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: QUERY_CONFIG.STALE_TIME,
      gcTime: QUERY_CONFIG.CACHE_TIME,
      retry: QUERY_CONFIG.RETRY_COUNT,
      refetchOnWindowFocus: QUERY_CONFIG.REFETCH_ON_FOCUS,
      refetchOnReconnect: QUERY_CONFIG.REFETCH_ON_RECONNECT,
    },
    mutations: {
      retry: 1,
      onError: (error) => {
        if (isDev) {
          console.error('[Mutation Error]', error);
        }
      },
    },
  },
});

/**
 * Query keys factory per type-safety e consistenza
 *
 * @example
 * // Utilizzo con useQuery
 * useQuery({
 *   queryKey: queryKeys.surveys.list({ status: 'active' }),
 *   queryFn: () => fetchSurveys({ status: 'active' })
 * })
 */
export const queryKeys = {
  /** Keys per le survey */
  surveys: {
    all: ['surveys'] as const,
    lists: () => [...queryKeys.surveys.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.surveys.lists(), filters] as const,
    details: () => [...queryKeys.surveys.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.surveys.details(), id] as const,
  },

  /** Keys per i viaggi */
  trips: {
    all: ['trips'] as const,
    lists: () => [...queryKeys.trips.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.trips.lists(), filters] as const,
    details: () => [...queryKeys.trips.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.trips.details(), id] as const,
  },

  /** Keys per statistiche */
  stats: {
    all: ['stats'] as const,
    dashboard: () => [...queryKeys.stats.all, 'dashboard'] as const,
    trends: (period: string) => [...queryKeys.stats.all, 'trends', period] as const,
  },

  /** Keys per autenticazione */
  auth: {
    user: ['auth', 'user'] as const,
    session: ['auth', 'session'] as const,
  },

  /** Keys per questionari pubblici */
  publicSurveys: {
    all: ['publicSurveys'] as const,
    byToken: (token: string) => [...queryKeys.publicSurveys.all, 'token', token] as const,
  },
} as const;

/**
 * Props per il QueryProvider
 */
interface QueryProviderProps {
  children: ReactNode;
}

/**
 * Provider wrapper per React Query con DevTools in development
 *
 * @example
 * // In main.tsx
 * <QueryProvider>
 *   <App />
 * </QueryProvider>
 */
export function QueryProvider({ children }: QueryProviderProps) {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {isDev && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}
